import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spacer } from 'app/member/layouts/generic';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { Button } from 'app/shared/button';
import { DateTimePicker } from 'app/shared/datepickers';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import { MultiSelect } from 'app/shared/select';
import { useAudienceService } from 'hooks/users/audience';
import useValidator from 'hooks/validation';
import { PushMessageValidator } from './validator';
import { useMobilePushMessageService } from 'hooks/messaging/mobile/message';
import { updateMobilePushMessageInStore } from 'store/actions/push/mobile';

const message_types = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'transactional', label: 'Transactional' }
];

export const MobilePushMessageForm = ({
  message_data = {},
  closeModal = () => {},
  onSave = () => {}
}) => {
  const dispatch = useDispatch();
  const { errors, form_is_valid, validateField } = useValidator(PushMessageValidator);
  const { fetchAudiences } = useAudienceService();
  const { createMessage, updateMessage } = useMobilePushMessageService();
  const { id: user_id } = useSelector((state) => state.user_data);

  const [audience_options, setAudienceOptions] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [schedule, setSchedule] = useState('');
  const [sub_title, setSubtitle] = useState('');
  const [title, setTitle] = useState('');
  const [tenant_id, setTenantId] = useState('');
  const [type, setType] = useState({});

  useEffect(() => {
    fetchAudiences().then(({ audiences }) => {
      setTenantId(audiences[0]?.tenant_id);
      setAudienceOptions(() => audiences.map((aud) => ({ value: aud.id, label: aud.name })));
    });
  }, []);

  useEffect(() => {
    if (!message_data || !Object.keys(message_data).length) return;
    setTitle(message_data.title);
    setMessage(message_data.message);
    setSubtitle(message_data.sub_title);
    setType(message_types.find((option) => option.value === message_data.type) || {});
    if (message_data.type === 'campaign') {
      setSchedule(message_data.schedule.date);
    }

    fetchAudiences().then(({ audiences }) => {
      const audience_by_id = audiences.reduce((s, a) => ({ ...s, [a.id]: a }), {});
      setAudienceOptions(() => audiences.map((aud) => ({ value: aud.id, label: aud.name })));
      if (message_data && message_data.type === 'campaign') {
        setAudiences(() =>
          (message_data.audiences || []).map((aud) => ({
            value: aud,
            label: audience_by_id[aud]?.name
          }))
        );
      }
    });
  }, [message_data.id]);

  useEffect(() => {
    validateField('message', message);
    validateField('sub_title', sub_title);
    validateField('title', title);
    validateField('type', type.value);

    validateField('schedule', schedule, { type: type.value });
    validateField('audiences', audiences, { type: type.value });
  }, [title, message, schedule, sub_title, audiences, type]);

  const clearForm = () => {
    setTitle('');
    setMessage('');
    setSubtitle('');
    setSchedule('');
    setType({});
    setAudiences([]);
  };

  const handleMessageChange = (val) => {
    if (val.length > 160) return;
    setMessage(val);
  };

  const submit = async () => {
    const data = {
      user_id,
      tenant_id,
      message,
      sub_title,
      title,
      audiences: audiences.map((audience) => audience.value),
      schedule: { date: schedule ? new Date(schedule) : new Date() },
      status: 'queued',
      type: type.value
    };

    setLoading(true);
    const { message: result } = message_data.id
      ? await updateMessage(message_data.id, { data })
      : await createMessage({ data });

    setLoading(false);
    if (!result) return;
    dispatch(updateMobilePushMessageInStore({ id: message_data.id, ...result }));
    onSave({ ...message_data, ...data, ...result });
    clearForm();
    closeModal();
  };

  return (
    <>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Title">
            <SimpleInput value={title} onInput={setTitle} error={errors.title} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Sub title">
            <SimpleInput value={sub_title} onInput={setSubtitle} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Type">
            <MultiSelect
              value={type}
              onChange={setType}
              options={message_types}
              error={errors.type}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Message">
            <TextArea
              value={message}
              onInput={handleMessageChange}
              error={errors.message}
              show_counter
              character_count={message.length}
              character_limit={160}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      {type.value === 'campaign' && (
        <>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem title="Audiences">
                <MultiSelect
                  value={audiences}
                  onChange={setAudiences}
                  options={audience_options}
                  error={errors.audiences}
                  isMulti
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem title="Schedule">
                <DateTimePicker
                  showTime
                  value={schedule}
                  onChange={setSchedule}
                  error={errors.schedule}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
        </>
      )}
      <Spacer multiple={4} />
      <GridRow num_of_columns={1}>
        <GridColumn />
        <GridColumn>
          <Button text="Save" disabled={!form_is_valid || loading} onClick={submit} />
        </GridColumn>
      </GridRow>
    </>
  );
};
